import { defineStore } from 'pinia';
import { useOrganizationStore } from './organizationStore';
import { apiRequestHandler } from '@/utils/api';
import {
  CreateStoredView,
  OrganizationService,
  StoredView,
  UpdateStoredView,
} from '@/api';

export interface StoredViewState {
  allViews: StoredView[];
  loadedAtLeastOnce: boolean;
}

export const useStoredViewStore = defineStore('storedView', {
  state: (): StoredViewState => ({
    allViews: [],
    loadedAtLeastOnce: false,
  }),
  actions: {
    async populateViews() {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await OrganizationService.findStoredViews<true>({
          path: { organizationId },
        });
        this.allViews = response.data.data ?? [];
        this.loadedAtLeastOnce = true;
        return response;
      });
    },

    async saveView(data: CreateStoredView) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await OrganizationService.createStoredView<true>({
          path: { organizationId },
          body: data,
        });
        this.allViews.unshift(response.data.data);
        return response.data;
      });
    },

    async updateView(viewId: StoredView['id'], data: UpdateStoredView) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await OrganizationService.updateStoredView<true>({
          path: { organizationId, storedViewId: viewId },
          body: data,
        });
        const index = this.allViews.findIndex((v) => v.id === viewId);
        if (index >= 0) this.allViews[index] = response.data.data;
        return response.data;
      });
    },

    async deleteView(viewId: StoredView['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        await OrganizationService.deleteStoredView<true>({
          path: { organizationId, storedViewId: viewId },
        });
        const index = this.allViews.findIndex((v) => v.id === viewId);
        if (index >= 0) this.allViews.splice(index, 1);
        return true;
      });
    },
  },
});
